import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Permission } from '@today/types'
import type { OfferListViewProps } from '@today/ui-domain'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { OFFERS_NAMESPACE } from '@/i18n'
import { offerKeys, offerService } from '@/services/offer.service'

const PAGE_SIZE = 20

/** `/offers` list state (TO-466 REQ-005): the page lives in the URL; "New offer" needs `offer.write`. */
export function useOfferList(): Omit<OfferListViewProps, 't'> {
	const { i18n } = useTranslation(OFFERS_NAMESPACE)
	const navigate = useNavigate()
	const [params, setParams] = useSearchParams()
	const { permissions } = useViewerPermissions()
	const page = Math.max(1, Number(params.get('page')) || 1)
	const query = useQuery({
		queryKey: offerKeys.list({ page, limit: PAGE_SIZE }),
		queryFn: () => offerService.list({ page, limit: PAGE_SIZE }),
		placeholderData: keepPreviousData,
		retry: false,
	})
	return {
		rows: (query.data?.data ?? []).map((o) => ({
			id: o.offerId,
			status: o.status,
			createdAt: new Date(o.createdAt).toLocaleString(i18n.language),
		})),
		page,
		pageCount: query.data?.totalPages ?? 0,
		error: query.isError,
		onPageChange: (next) => setParams({ page: String(next) }),
		onOpen: (id) => void navigate(`/offers/${id}`),
		onRetry: () => void query.refetch(),
		onCreate: permissions.includes(Permission.OFFER_WRITE) ? () => void navigate('/offers/new') : undefined,
	}
}
